import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/lib/supabaseClient';
import ImageUploader from '@/components/base/ImageUploader';
import { invalidateSiteSettings } from '@/hooks/useSiteSettings';

/* ─── Types ─────────────────────────────────────────────── */
interface Section {
  id: string;
  section_key: string;
  title: string | null;
  subtitle: string | null;
  description: string | null;
  description2: string | null;
  badge_text: string | null;
  cta_text: string | null;
  image_url: string | null;
  image_url2: string | null;
  extra_json: unknown;
  is_active: boolean;
  updated_at: string;
}

interface GalleryImage { src: string; alt: string; }
interface FeatureItem { icon: string; title: string; description: string; color: string; }
interface TestimonialItem { name: string; role: string; text: string; rating: number; avatar: string; }

type SectionKey = 'hero_bg' | 'logo' | 'restaurant_info' | 'about' | 'features' | 'gallery' | 'testimonials' | 'contact' | 'footer';

const SECTION_LABELS: Record<SectionKey, { label: string; icon: string; desc: string }> = {
  hero_bg:         { label: 'Hero Arka Plan',      icon: 'ri-image-line',        desc: 'Ana sayfa hero bölümü arka plan görseli' },
  logo:            { label: 'Logo',                icon: 'ri-image-edit-line',   desc: 'Site logosu (normal ve beyaz versiyon)' },
  restaurant_info: { label: 'Restoran Bilgileri',  icon: 'ri-store-line',        desc: 'İsim, telefon, adres, Just Eat linki' },
  about:           { label: 'Hakkımızda',          icon: 'ri-store-2-line',      desc: 'Tanıtım metni ve görseller' },
  features:        { label: 'Özellikler',          icon: 'ri-star-line',         desc: 'Neden bizi seçmelisiniz kartları' },
  gallery:         { label: 'Galeri',              icon: 'ri-image-2-line',      desc: 'Yemek fotoğrafları galerisi' },
  testimonials:    { label: 'Yorumlar',            icon: 'ri-chat-quote-line',   desc: 'Müşteri yorumları' },
  contact:         { label: 'Kontakt Bölümü',      icon: 'ri-map-pin-line',      desc: 'İletişim bölümü başlık ve açıklama' },
  footer:          { label: 'Footer',              icon: 'ri-layout-bottom-line',desc: 'Alt bilgi metni ve sosyal medya' },
};

/* ─── Helpers ────────────────────────────────────────────── */
const inputCls = 'w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-50 transition-all';
const labelCls = 'block text-xs font-semibold text-gray-600 mb-1.5';

/* ─── Component ──────────────────────────────────────────── */
const HomepageTab = () => {
  const [sections, setSections] = useState<Section[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState<SectionKey>('hero_bg');
  const [form, setForm] = useState<Partial<Section>>({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Site settings (logo, hero bg, restaurant info, footer)
  const [siteSettings, setSiteSettings] = useState<Record<string, string>>({});
  const [settingsForm, setSettingsForm] = useState<Record<string, string>>({});
  const [savingSettings, setSavingSettings] = useState(false);

  // Gallery state
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);

  // Features state
  const [features, setFeatures] = useState<FeatureItem[]>([]);

  // Testimonials state
  const [testimonials, setTestimonials] = useState<TestimonialItem[]>([]);

  const showToast = useCallback((type: 'success' | 'error', text: string) => {
    setToast({ type, text });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3500);
  }, []);

  /* ── Fetch ── */
  const fetchSections = useCallback(async () => {
    setLoading(true);
    const [{ data: secs }, { data: settings }] = await Promise.all([
      supabase.from('homepage_sections').select('*').order('id'),
      supabase.from('site_settings').select('key, value'),
    ]);
    if (secs) setSections(secs as Section[]);
    if (settings) {
      const map: Record<string, string> = {};
      settings.forEach((r) => { map[r.key] = r.value ?? ''; });
      setSiteSettings(map);
      setSettingsForm(map);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchSections(); }, [fetchSections]);

  /* ── Sync form when section changes ── */
  useEffect(() => {
    const sec = sections.find((s) => s.section_key === activeSection);
    if (sec) {
      setForm({ ...sec });
      if (activeSection === 'gallery') {
        try { setGalleryImages((sec.extra_json as GalleryImage[]) || []); } catch { setGalleryImages([]); }
      }
      if (activeSection === 'features') {
        try { setFeatures((sec.extra_json as FeatureItem[]) || []); } catch { setFeatures([]); }
      }
      if (activeSection === 'testimonials') {
        try { setTestimonials((sec.extra_json as TestimonialItem[]) || []); } catch { setTestimonials([]); }
      }
    }
    // For settings-based sections, sync settingsForm
    if (['hero_bg', 'logo', 'restaurant_info', 'footer'].includes(activeSection)) {
      setSettingsForm({ ...siteSettings });
    }
  }, [activeSection, sections, siteSettings]);

  /* ── Save section ── */
  const handleSaveSection = async () => {
    const sec = sections.find((s) => s.section_key === activeSection);
    if (!sec) return;

    let extra = form.extra_json;
    if (activeSection === 'gallery') extra = galleryImages;
    if (activeSection === 'features') extra = features;
    if (activeSection === 'testimonials') extra = testimonials;

    setSaving(true);
    const { error } = await supabase
      .from('homepage_sections')
      .update({
        title: form.title,
        subtitle: form.subtitle,
        description: form.description,
        description2: form.description2,
        badge_text: form.badge_text,
        image_url: form.image_url,
        image_url2: form.image_url2,
        extra_json: extra,
        updated_at: new Date().toISOString(),
      })
      .eq('section_key', activeSection);
    setSaving(false);
    if (error) { showToast('error', 'Kayıt sırasında hata oluştu.'); return; }
    showToast('success', 'Bölüm güncellendi!');
    fetchSections();
  };

  /* ── Save site settings ── */
  const handleSaveSettings = async (keys: string[]) => {
    setSavingSettings(true);
    const updates = keys.map((k) =>
      supabase.from('site_settings').update({ value: settingsForm[k], updated_at: new Date().toISOString() }).eq('key', k)
    );
    await Promise.all(updates);
    setSavingSettings(false);
    invalidateSiteSettings();
    showToast('success', 'Ayarlar kaydedildi!');
    fetchSections();
  };

  /* ─── Gallery helpers ─── */
  const addGalleryImage = () => setGalleryImages((prev) => [...prev, { src: '', alt: '' }]);
  const removeGalleryImage = (i: number) => setGalleryImages((prev) => prev.filter((_, idx) => idx !== i));
  const updateGalleryImage = (i: number, field: 'src' | 'alt', val: string) =>
    setGalleryImages((prev) => prev.map((img, idx) => idx === i ? { ...img, [field]: val } : img));

  /* ─── Testimonials helpers ─── */
  const addTestimonial = () => setTestimonials((prev) => [...prev, { name: '', role: '', text: '', rating: 5, avatar: '' }]);
  const removeTestimonial = (i: number) => setTestimonials((prev) => prev.filter((_, idx) => idx !== i));
  const updateTestimonial = (i: number, field: keyof TestimonialItem, val: string | number) =>
    setTestimonials((prev) => prev.map((t, idx) => idx === i ? { ...t, [field]: val } : t));

  /* ─── Features helpers ─── */
  const addFeature = () => setFeatures((prev) => [...prev, { icon: 'ri-star-line', title: '', description: '', color: 'bg-amber-50 text-amber-600' }]);
  const removeFeature = (i: number) => setFeatures((prev) => prev.filter((_, idx) => idx !== i));
  const updateFeature = (i: number, field: keyof FeatureItem, val: string) =>
    setFeatures((prev) => prev.map((f, idx) => idx === i ? { ...f, [field]: val } : f));

  const sf = (key: string) => settingsForm[key] ?? '';
  const setSf = (key: string, val: string) => setSettingsForm((prev) => ({ ...prev, [key]: val }));

  /* ─── Render sections ─── */

  const renderHeroBg = () => (
    <div className="flex flex-col gap-5">
      <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-100 rounded-xl">
        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
          <i className="ri-image-line text-amber-500 text-sm"></i>
        </div>
        <div>
          <p className="text-xs font-semibold text-amber-700">Önerilen boyut: <span className="font-bold">1920 × 1080 px</span> (16:9 yatay)</p>
          <p className="text-xs text-amber-600 mt-0.5">Tam ekran arka plan — en az 1440px genişlik önerilir. Dosya boyutu max 5MB.</p>
        </div>
      </div>
      <ImageUploader
        value={sf('hero_bg_url')}
        onChange={(url) => setSf('hero_bg_url', url)}
        label="Hero Arka Plan Görseli"
        placeholder="https://... veya fotoğraf yükle"
        folder="hero"
      />
      <p className="text-xs text-gray-400">Bu görsel ana sayfanın tam ekran arka planında kullanılır. Geniş (landscape) format önerilir.</p>
      <SaveBtn onClick={() => handleSaveSettings(['hero_bg_url'])} loading={savingSettings} />
    </div>
  );

  const renderLogo = () => (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-3 px-4 py-3 bg-orange-50 border border-orange-100 rounded-xl">
        <i className="ri-palette-line text-orange-500"></i>
        <p className="text-xs text-orange-700">
          Logo boyutu, hizalama ve operasyon ekranları için <strong>Markalama & Medya</strong> sekmesini kullanın.
        </p>
      </div>
      <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-100 rounded-xl">
        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
          <i className="ri-image-edit-line text-amber-500 text-sm"></i>
        </div>
        <div>
          <p className="text-xs font-semibold text-amber-700">Önerilen: <span className="font-bold">dikey PNG</span>, şeffaf arka plan</p>
          <p className="text-xs text-amber-600 mt-0.5">Koyu zemin için beyaz+kırmızı, açık zemin için koyu+kırmızı versiyon. Max 2MB.</p>
        </div>
      </div>
      <ImageUploader
        value={sf('logo_url')}
        onChange={(url) => setSf('logo_url', url)}
        label="Logo — koyu arka plan (hero / footer)"
        placeholder="Beyaz yazılı logo"
        folder="logo"
      />
      <ImageUploader
        value={sf('logo_inverse_url')}
        onChange={(url) => setSf('logo_inverse_url', url)}
        label="Logo — açık arka plan (navbar / menü)"
        placeholder="Koyu yazılı logo"
        folder="logo"
      />
      <p className="text-xs text-gray-400">
        Varsayılan olarak Yawas Kebab logoları yüklüdür. Yeni dosya yüklerseniz bu alanlar öncelik alır.
      </p>
      <SaveBtn onClick={() => handleSaveSettings(['logo_url', 'logo_inverse_url'])} loading={savingSettings} />
    </div>
  );

  const renderRestaurantInfo = () => (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Restoran Adı</label>
          <input type="text" value={sf('restaurant_name')} onChange={(e) => setSf('restaurant_name', e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Slogan</label>
          <input type="text" value={sf('restaurant_tagline')} onChange={(e) => setSf('restaurant_tagline', e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Telefon</label>
          <input type="text" value={sf('restaurant_phone')} onChange={(e) => setSf('restaurant_phone', e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>E-posta</label>
          <input type="text" value={sf('restaurant_email')} onChange={(e) => setSf('restaurant_email', e.target.value)} className={inputCls} />
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Adres</label>
          <input type="text" value={sf('restaurant_address')} onChange={(e) => setSf('restaurant_address', e.target.value)} className={inputCls} />
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Çalışma Saatleri</label>
          <textarea
            value={sf('restaurant_hours')}
            onChange={(e) => setSf('restaurant_hours', e.target.value)}
            rows={4}
            placeholder={'Montag – Samstag: 11:00 Uhr – 13:30 Uhr\n17:00 Uhr – 19:30 Uhr\nSonntag: geschlossen'}
            className={`${inputCls} resize-none font-mono text-sm`}
          />
          <p className="text-xs text-gray-400 mt-1.5">
            Her satır: <span className="font-mono">Gün: saat</span>. Etiket yoksa üstteki günün ikinci saat satırı olur.
          </p>
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Kısa Açıklama</label>
          <textarea value={sf('restaurant_description')} onChange={(e) => setSf('restaurant_description', e.target.value)} rows={3} maxLength={500} className={`${inputCls} resize-none`} />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4">
        <div>
          <label className={labelCls}>Just Eat Sipariş Linki</label>
          <input
            type="url"
            value={sf('just_eat_url')}
            onChange={(e) => setSf('just_eat_url', e.target.value)}
            placeholder="https://www.just-eat.ch/..."
            className={inputCls}
          />
          <p className="text-xs text-gray-400 mt-1.5">
            &quot;Jetzt bestellen&quot; butonları bu adrese yönlendirilir.
          </p>
        </div>
      </div>
      <SaveBtn onClick={() => handleSaveSettings(['restaurant_name','restaurant_tagline','restaurant_phone','restaurant_email','restaurant_address','restaurant_hours','restaurant_description','just_eat_url'])} loading={savingSettings} />
    </div>
  );

  const renderAbout = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      <div>
        <label className={labelCls}>Bölüm Etiketi</label>
        <input type="text" value={form.subtitle || ''} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className={inputCls} />
      </div>
      <div>
        <label className={labelCls}>Rozet Metni</label>
        <input type="text" value={form.badge_text || ''} onChange={(e) => setForm({ ...form, badge_text: e.target.value })} className={inputCls} />
      </div>
      <div className="md:col-span-2">
        <label className={labelCls}>Başlık</label>
        <input type="text" value={form.title || ''} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
      </div>
      <div className="md:col-span-2">
        <label className={labelCls}>1. Paragraf</label>
        <textarea value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} maxLength={500} className={`${inputCls} resize-none`} />
      </div>
      <div className="md:col-span-2">
        <label className={labelCls}>2. Paragraf</label>
        <textarea value={form.description2 || ''} onChange={(e) => setForm({ ...form, description2: e.target.value })} rows={3} maxLength={500} className={`${inputCls} resize-none`} />
      </div>
      <div className="md:col-span-2">
        <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-100 rounded-xl mb-4">
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
            <i className="ri-image-line text-amber-500 text-sm"></i>
          </div>
          <div>
            <p className="text-xs font-semibold text-amber-700">Önerilen boyut: <span className="font-bold">600 × 700 px</span> (dikey/portrait)</p>
            <p className="text-xs text-amber-600 mt-0.5">İki görsel yan yana gösterilir. Aynı boyut ve oran kullanılması önerilir. Max 5MB.</p>
          </div>
        </div>
      </div>
      <ImageUploader value={form.image_url || ''} onChange={(url) => setForm({ ...form, image_url: url })} label="Sol Görsel (600 × 700 px önerilir)" folder="about" />
      <ImageUploader value={form.image_url2 || ''} onChange={(url) => setForm({ ...form, image_url2: url })} label="Sağ Görsel (600 × 700 px önerilir)" folder="about" />
    </div>
  );

  const renderFeatures = () => (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Bölüm Etiketi</label>
          <input type="text" value={form.subtitle || ''} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Başlık</label>
          <input type="text" value={form.title || ''} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Alt Açıklama</label>
          <input type="text" value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} />
        </div>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-gray-600">Özellik Kartları ({features.length})</p>
        <button onClick={addFeature} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-orange-50 text-orange-600 text-xs font-semibold hover:bg-orange-100 transition-all cursor-pointer whitespace-nowrap">
          <i className="ri-add-line"></i> Kart Ekle
        </button>
      </div>

      <div className="flex flex-col gap-3">
        {features.map((f, i) => (
          <div key={i} className="bg-gray-50 rounded-xl p-4 border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-gray-500">Kart {i + 1}</span>
              <button onClick={() => removeFeature(i)} className="w-6 h-6 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 cursor-pointer transition-colors">
                <i className="ri-delete-bin-line text-xs"></i>
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>İkon (Remix Icon sınıfı)</label>
                <input type="text" value={f.icon} onChange={(e) => updateFeature(i, 'icon', e.target.value)} placeholder="ri-timer-line" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Renk sınıfı</label>
                <input type="text" value={f.color} onChange={(e) => updateFeature(i, 'color', e.target.value)} placeholder="bg-amber-50 text-amber-600" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Başlık</label>
                <input type="text" value={f.title} onChange={(e) => updateFeature(i, 'title', e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Açıklama</label>
                <input type="text" value={f.description} onChange={(e) => updateFeature(i, 'description', e.target.value)} className={inputCls} />
              </div>
            </div>
          </div>
        ))}
        {features.length === 0 && (
          <div className="text-center py-6 text-gray-400 text-sm border-2 border-dashed border-gray-200 rounded-xl">
            Henüz kart yok. "Kart Ekle" butonuna tıklayın.
          </div>
        )}
      </div>
    </div>
  );

  const renderGallery = () => (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Bölüm Etiketi</label>
          <input type="text" value={form.subtitle || ''} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Başlık</label>
          <input type="text" value={form.title || ''} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
        </div>
      </div>

      <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-100 rounded-xl">
        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
          <i className="ri-image-2-line text-amber-500 text-sm"></i>
        </div>
        <div>
          <p className="text-xs font-semibold text-amber-700">Önerilen boyut: <span className="font-bold">800 × 600 px</span> (yatay) veya <span className="font-bold">600 × 800 px</span> (dikey)</p>
          <p className="text-xs text-amber-600 mt-0.5">Galeri ızgara düzeninde gösterilir. Tutarlı oran kullanılması önerilir. Max 5MB/fotoğraf.</p>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-gray-600">Galeri Fotoğrafları ({galleryImages.length})</p>
        <button onClick={addGalleryImage} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-orange-50 text-orange-600 text-xs font-semibold hover:bg-orange-100 transition-all cursor-pointer whitespace-nowrap">
          <i className="ri-add-line"></i> Fotoğraf Ekle
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {galleryImages.map((img, i) => (
          <div key={i} className="bg-gray-50 rounded-xl p-3 border border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-500">Fotoğraf {i + 1}</span>
              <button onClick={() => removeGalleryImage(i)} className="w-6 h-6 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 cursor-pointer transition-colors">
                <i className="ri-delete-bin-line text-xs"></i>
              </button>
            </div>
            <ImageUploader
              value={img.src}
              onChange={(url) => updateGalleryImage(i, 'src', url)}
              label=""
              placeholder="https://... veya fotoğraf yükle"
              folder="gallery"
            />
            <div className="mt-2">
              <label className={labelCls}>Alt Metin (SEO)</label>
              <input type="text" value={img.alt} onChange={(e) => updateGalleryImage(i, 'alt', e.target.value)} placeholder="Fotoğraf açıklaması" className={inputCls} />
            </div>
          </div>
        ))}
        {galleryImages.length === 0 && (
          <div className="sm:col-span-2 text-center py-8 text-gray-400 text-sm border-2 border-dashed border-gray-200 rounded-xl">
            Henüz fotoğraf yok. "Fotoğraf Ekle" butonuna tıklayın.
          </div>
        )}
      </div>
    </div>
  );

  const renderTestimonials = () => (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Bölüm Etiketi</label>
          <input type="text" value={form.subtitle || ''} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Başlık</label>
          <input type="text" value={form.title || ''} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
        </div>
      </div>

      <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-100 rounded-xl">
        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
          <i className="ri-user-line text-amber-500 text-sm"></i>
        </div>
        <div>
          <p className="text-xs font-semibold text-amber-700">Profil fotoğrafı önerilen boyut: <span className="font-bold">200 × 200 px</span> (kare)</p>
          <p className="text-xs text-amber-600 mt-0.5">Yuvarlak olarak gösterilir. PNG veya JPG, max 2MB. Yüz net görünmeli.</p>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-gray-600">Müşteri Yorumları ({testimonials.length})</p>
        <button onClick={addTestimonial} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-orange-50 text-orange-600 text-xs font-semibold hover:bg-orange-100 transition-all cursor-pointer whitespace-nowrap">
          <i className="ri-add-line"></i> Yorum Ekle
        </button>
      </div>

      <div className="flex flex-col gap-4">
        {testimonials.map((t, i) => (
          <div key={i} className="bg-gray-50 rounded-xl p-4 border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-gray-500">Yorum {i + 1}</span>
              <button onClick={() => removeTestimonial(i)} className="w-6 h-6 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 cursor-pointer transition-colors">
                <i className="ri-delete-bin-line text-xs"></i>
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <div>
                <label className={labelCls}>İsim</label>
                <input type="text" value={t.name} onChange={(e) => updateTestimonial(i, 'name', e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Unvan / Rol</label>
                <input type="text" value={t.role} onChange={(e) => updateTestimonial(i, 'role', e.target.value)} className={inputCls} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Yorum Metni</label>
                <textarea value={t.text} onChange={(e) => updateTestimonial(i, 'text', e.target.value)} rows={2} maxLength={500} className={`${inputCls} resize-none`} />
              </div>
              <div>
                <label className={labelCls}>Puan (1-5)</label>
                <select value={t.rating} onChange={(e) => updateTestimonial(i, 'rating', Number(e.target.value))} className={inputCls}>
                  {[5,4,3,2,1].map((n) => <option key={n} value={n}>{n} Yıldız</option>)}
                </select>
              </div>
            </div>
            <ImageUploader
              value={t.avatar}
              onChange={(url) => updateTestimonial(i, 'avatar', url)}
              label="Profil Fotoğrafı"
              placeholder="https://... veya fotoğraf yükle"
              folder="testimonials"
              previewShape="circle"
            />
          </div>
        ))}
        {testimonials.length === 0 && (
          <div className="text-center py-6 text-gray-400 text-sm border-2 border-dashed border-gray-200 rounded-xl">
            Henüz yorum yok. "Yorum Ekle" butonuna tıklayın.
          </div>
        )}
      </div>
    </div>
  );

  const renderFooter = () => (
    <div className="flex flex-col gap-5">
      <div>
        <label className={labelCls}>Footer Açıklama Metni</label>
        <textarea value={sf('footer_description')} onChange={(e) => setSf('footer_description', e.target.value)} rows={3} maxLength={500} className={`${inputCls} resize-none`} />
      </div>
      <div>
        <label className={labelCls}>Catering Metni</label>
        <input type="text" value={sf('footer_catering_text')} onChange={(e) => setSf('footer_catering_text', e.target.value)} className={inputCls} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Instagram Linki</label>
          <input type="text" value={sf('social_instagram')} onChange={(e) => setSf('social_instagram', e.target.value)} placeholder="https://instagram.com/..." className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Facebook Linki</label>
          <input type="text" value={sf('social_facebook')} onChange={(e) => setSf('social_facebook', e.target.value)} placeholder="https://facebook.com/..." className={inputCls} />
        </div>
      </div>
      <SaveBtn onClick={() => handleSaveSettings(['footer_description','footer_catering_text','social_instagram','social_facebook'])} loading={savingSettings} />
    </div>
  );

  const renderContact = () => (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Bölüm Etiketi</label>
          <input type="text" value={form.subtitle || ''} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className={inputCls} placeholder="Kontakt" />
        </div>
        <div>
          <label className={labelCls}>Başlık</label>
          <input type="text" value={form.title || ''} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} placeholder="Kontaktieren Sie uns" />
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Açıklama Metni</label>
          <textarea value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} maxLength={500} className={`${inputCls} resize-none`} placeholder="Haben Sie Fragen..." />
        </div>
      </div>
      <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 text-sm text-amber-700">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-information-line"></i></div>
          <span className="font-semibold">Telefon, e-posta ve adres bilgileri</span>
        </div>
        <p className="text-xs text-amber-600">Bu bilgiler "Restoran Bilgileri" bölümünden yönetilmektedir. Oradan güncelleyebilirsiniz.</p>
      </div>
    </div>
  );

  const isSettingsSection = ['hero_bg', 'logo', 'restaurant_info', 'footer'].includes(activeSection);

  const renderForm = () => {
    switch (activeSection) {
      case 'hero_bg':         return renderHeroBg();
      case 'logo':            return renderLogo();
      case 'restaurant_info': return renderRestaurantInfo();
      case 'about':           return renderAbout();
      case 'features':        return renderFeatures();
      case 'gallery':         return renderGallery();
      case 'testimonials':    return renderTestimonials();
      case 'contact':         return renderContact();
      case 'footer':          return renderFooter();
      default:                return null;
    }
  };

  const info = SECTION_LABELS[activeSection];

  return (
    <div className="flex flex-col gap-6 max-w-4xl">
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-1">Ana Sayfa İçeriği</h2>
        <p className="text-gray-500 text-sm">Sitenin tüm bölümlerini buradan düzenleyebilirsiniz</p>
      </div>

      {toast && (
        <div className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium ${
          toast.type === 'success' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-600 border border-red-100'
        }`}>
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
            <i className={toast.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'}></i>
          </div>
          {toast.text}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : (
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left nav */}
          <div className="flex flex-row lg:flex-col gap-1.5 lg:w-52 flex-shrink-0 overflow-x-auto lg:overflow-visible pb-1 lg:pb-0">
            {(Object.keys(SECTION_LABELS) as SectionKey[]).map((key) => {
              const inf = SECTION_LABELS[key];
              const isActive = activeSection === key;
              return (
                <button
                  key={key}
                  onClick={() => setActiveSection(key)}
                  className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-all cursor-pointer whitespace-nowrap text-left w-full flex-shrink-0 ${
                    isActive ? 'bg-orange-500 text-white' : 'bg-white border border-gray-100 text-gray-600 hover:border-orange-200 hover:text-orange-600'
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className={`${inf.icon} text-sm`}></i>
                  </div>
                  {inf.label}
                </button>
              );
            })}
          </div>

          {/* Form area */}
          <div className="flex-1 bg-white rounded-2xl border border-gray-100 p-6">
            <div className="flex items-center gap-3 mb-6 pb-5 border-b border-gray-100">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-orange-50 text-orange-500 flex-shrink-0">
                <i className={`${info.icon} text-lg`}></i>
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">{info.label}</h3>
                <p className="text-xs text-gray-500">{info.desc}</p>
              </div>
            </div>

            {renderForm()}

            {/* Save button — only for section-based forms */}
            {!isSettingsSection && (
              <div className="flex items-center gap-3 mt-6 pt-5 border-t border-gray-100">
                <button
                  onClick={handleSaveSection}
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold transition-all cursor-pointer disabled:opacity-50 whitespace-nowrap"
                >
                  {saving ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <div className="w-4 h-4 flex items-center justify-center"><i className="ri-save-line"></i></div>
                  )}
                  Değişiklikleri Kaydet
                </button>
                <p className="text-xs text-gray-400">Kaydedilince ana sayfaya anında yansır</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Save Button helper ─── */
const SaveBtn = ({ onClick, loading }: { onClick: () => void; loading: boolean }) => (
  <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
    <button
      onClick={onClick}
      disabled={loading}
      className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold transition-all cursor-pointer disabled:opacity-50 whitespace-nowrap"
    >
      {loading ? (
        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
      ) : (
        <div className="w-4 h-4 flex items-center justify-center"><i className="ri-save-line"></i></div>
      )}
      Kaydet
    </button>
    <p className="text-xs text-gray-400">Kaydedilince siteye anında yansır</p>
  </div>
);

export default HomepageTab;
